import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { Injectable } from '@angular/core';

import { RequestService } from '@shared/services/request/request.service';
import { BUSINESS_ENTITY, GROUP_TYPE } from '../enums';
import { IEntityItemGroupId, IEntityItemId, IEntityItemInstanceId } from '../types';
import { IGroupDto, IGroupType } from '@main/interfaces';
import { ResponseGetGroup } from './groups.service.types';
import { EntityGroup } from '../models';
import { EntityHelpers } from '../helpers/entity.helpers';

@Injectable()
export class GroupsService {
    private groupsCache: EntityGroup[] = [];

    constructor(private requestService: RequestService) {}

    fetchGroup(id: IEntityItemId): Observable<EntityGroup> {
        const cachedItem = this.groupsCache.find((v) => v.id === id);

        if (cachedItem) {
            return of(cachedItem);
        }

        const url = `favorites/datapoints/${id}`;

        return this.requestService.get<IGroupDto>(url).pipe(
            map((response) => {
                const groupEntity = this.deserializeGroup(response);
                this.cache(groupEntity);

                return groupEntity;
            }),
        );
    }

    fetchGroupsList(
        entityType: BUSINESS_ENTITY,
        opts: Partial<{ search: string; limit: number; type: IGroupType; strict: boolean }> = {},
    ): Observable<EntityGroup[]> {
        const params: Record<string, any> = {
            size: opts.limit || 100,
        };

        if (entityType) {
            params.entityTypes = entityType;
        }

        if (opts.search) {
            params.q = opts.search;
        }

        if (opts.type) {
            params.types = opts.type;
        }

        if (opts.strict) {
            params.strict = true;
        }

        const url = 'favorites/datapoints';

        return this.requestService.get<ResponseGetGroup>(url, { params }).pipe(
            map((response) => {
                const items = response?.elements || [];

                const result = items.map((item) => {
                    const rootEntity = this.deserializeGroup(item);
                    this.cache(rootEntity);

                    return rootEntity;
                });

                result[Symbol.for('total')] = response?.total || 0;

                return result;
            }),
            catchError(() => {
                console.error('Error fetching groups list');

                return of([]);
            }),
        );
    }

    createGroup(
        entityType: BUSINESS_ENTITY,
        name: string,
        ids: IEntityItemId[],
        categoryBenchmarkId: IEntityItemId,
        comparison = false,
    ): Observable<IGroupDto> {
        const data = this.serializeGroupUpdateRequest(entityType, null, name, ids, categoryBenchmarkId, comparison);
        const url = `favorites/datapoints/`;

        return this.requestService.post<IGroupDto>(url, data);
    }

    updateGroup(
        entityType: BUSINESS_ENTITY,
        id: IEntityItemGroupId,
        name: string,
        ids: IEntityItemId[],
        comparison: boolean,
        categoryBenchmarkId: IEntityItemId,
    ): Observable<IGroupDto> {
        const data: IGroupDto = this.serializeGroupUpdateRequest(
            entityType,
            id,
            name,
            ids,
            categoryBenchmarkId,
            comparison,
        );
        const url = `favorites/datapoints/${id}`;

        return this.requestService.put<IGroupDto>(url, data);
    }

    deleteGroup(entityType: BUSINESS_ENTITY, id: IEntityItemId): Observable<void> {
        const url = `favorites/datapoints/${id}?entityType=${entityType}`;

        return this.requestService.delete(url);
    }

    resetCache(): void {
        this.groupsCache = [];
    }

    private serializeGroupUpdateRequest(
        entityType: BUSINESS_ENTITY,
        id: IEntityItemGroupId,
        name: string,
        ids: IEntityItemId[],
        categoryBenchmarkId: IEntityItemId,
        comparison: boolean,
    ): IGroupDto {
        let singleInstancesIds = [];
        let groupsIds = [];

        switch (entityType) {
            case BUSINESS_ENTITY.Keyword:
                singleInstancesIds = ids;
                break;

            default:
                singleInstancesIds = ids.filter((v) => EntityHelpers.isSingleInstanceId(v)) as IEntityItemInstanceId[];

                groupsIds = ids.filter((v) => EntityHelpers.isGroupId(v)) as IEntityItemGroupId[];
        }

        const result: IGroupDto = {
            id,
            name,
            details: {
                entityType,
                type: comparison ? IGroupType.COMPARISON : IGroupType.GROUP,
                instances: singleInstancesIds,
                favoriteIds: groupsIds,
            },
        };

        if (categoryBenchmarkId) {
            result.details.categoryId = categoryBenchmarkId;
            result.details.entityType = BUSINESS_ENTITY.Benchmark;
        }

        return result;
    }

    private deserializeGroup(data: IGroupDto): EntityGroup {
        const {
            details: { type, entityType: groupEntityType, categoryId },
            relations = [],
        } = data;

        const entityType = groupEntityType === BUSINESS_ENTITY.Benchmark ? BUSINESS_ENTITY.Advertiser : groupEntityType;
        const subGroups = relations ? relations.map((v) => v.id && this.deserializeGroup(v)) : [];
        const icon = type === IGroupType.GROUP ? ':groupGeneric' : ':groupComparison';

        const comparisonCategoryBenchmark = !!categoryId;

        return new EntityGroup({
            groupType:
                type === IGroupType.GROUP
                    ? GROUP_TYPE.REGULAR
                    : comparisonCategoryBenchmark
                    ? GROUP_TYPE.COMPARISON_CATEGORY_BENCHMARK
                    : GROUP_TYPE.COMPARISON,
            entityType,
            id: data.id,
            title: data.name,
            icon,
            items: subGroups,
            dto: data,
            resolved: false,
            virtual: false,
        });
    }

    private cache(item: EntityGroup): void {
        if (!this.groupsCache.find((v) => v.id === item.id)) {
            this.groupsCache.push(item);
        }
    }
}
